import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext.jsx";
import Navigation from "./components/Navigation.jsx";
import ProductLanding from "./components/ProductLanding.jsx";
import ProductsPage from "./components/ProductsPage.jsx";
import AdminPage from "./components/AdminPage.jsx";
import AgeVerification from "./components/AgeVerification.jsx";
import RegisterForm from "./components/auth/RegisterForm.jsx";
import LoginForm from "./components/auth/LoginForm.jsx";
import UserProfile from "./components/user/UserProfile.jsx";
import ForgotPassword from "./components/auth/ForgotPassword.jsx";
import ResetPassword from "./components/auth/ResetPassword.jsx";
import EmailVerification from "./components/auth/EmailVerification.jsx";
import CheckoutPage from "./components/CheckoutPage.jsx";
import OrderConfirmation from "./components/OrderConfirmation.jsx";
import OrderHistory from "./components/OrderHistory.jsx";
import CustomerService from "./components/policies/CustomerService.jsx";
import ReturnPolicy from "./components/policies/ReturnPolicy.jsx";
import RefundPolicy from "./components/policies/RefundPolicy.jsx";
import CancellationPolicy from "./components/policies/CancellationPolicy.jsx";
import PrivacyPolicy from "./components/policies/PrivacyPolicy.jsx";
import TermsAndConditions from "./components/policies/TermsAndConditions.jsx";
import "./App.css";
import { Toaster } from "react-hot-toast";

// Component to reset scroll position on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  const [cartItems, setCartItems] = useState(() => {
    const savedCart = localStorage.getItem("cartItems");
    return savedCart ? JSON.parse(savedCart) : [];
  });

  useEffect(() => {
    localStorage.setItem("cartItems", JSON.stringify(cartItems));
  }, [cartItems]);

  const handleUpdateQuantity = (cartItemKey, newQuantity) => {
    if (newQuantity < 1) return;
    setCartItems((prevCart) =>
      prevCart.map((item) => {
        const key = item.cartItemId ?? item.id;
        return key === cartItemKey ? { ...item, quantity: newQuantity } : item;
      }),
    );
  };

  const handleRemoveItem = (cartItemKey) => {
    setCartItems((prevCart) =>
      prevCart.filter((item) => (item.cartItemId ?? item.id) !== cartItemKey),
    );
  };

  useEffect(() => {
    // Check initial dark mode preference
    if (
      localStorage.theme === "dark" ||
      (!("theme" in localStorage) &&
        window.matchMedia("(prefers-color-scheme: dark)").matches)
    ) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, []);

  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <Toaster position="top-center" gutter={8} />
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
          <Navigation
            cartItems={cartItems}
            handleUpdateQuantity={handleUpdateQuantity}
            handleRemoveItem={handleRemoveItem}
          />
          <main className="pt-16 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <AgeVerification />
              <Routes>
                <Route
                  path="/"
                  element={
                    <ProductLanding
                      cartItems={cartItems}
                      setCartItems={setCartItems}
                    />
                  }
                />
                <Route
                  path="/shop"
                  element={
                    <ProductsPage
                      cartItems={cartItems}
                      setCartItems={setCartItems}
                      handleUpdateQuantity={handleUpdateQuantity}
                      handleRemoveItem={handleRemoveItem}
                    />
                  }
                />
                <Route
                  path="/checkout"
                  element={
                    <CheckoutPage
                      cartItems={cartItems}
                      handleUpdateQuantity={handleUpdateQuantity}
                      handleRemoveItem={handleRemoveItem}
                    />
                  }
                />
                <Route
                  path="/checkout-success"
                  element={
                    <OrderConfirmation
                      cartItems={cartItems}
                      setCartItems={setCartItems}
                      handleUpdateQuantity={handleUpdateQuantity}
                      handleRemoveItem={handleRemoveItem}
                    />
                  }
                />
                <Route
                  path="/order-confirmation"
                  element={
                    <OrderConfirmation
                      cartItems={cartItems}
                      setCartItems={setCartItems}
                      handleUpdateQuantity={handleUpdateQuantity}
                      handleRemoveItem={handleRemoveItem}
                    />
                  }
                />
                <Route
                  path="/order-history"
                  element={
                    <OrderHistory
                      cartItems={cartItems}
                      handleUpdateQuantity={handleUpdateQuantity}
                      handleRemoveItem={handleRemoveItem}
                    />
                  }
                />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/login" element={<LoginForm />} />
                <Route path="/register" element={<RegisterForm />} />
                <Route path="/profile" element={<UserProfile />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route
                  path="/reset-password/:token"
                  element={<ResetPassword />}
                />
                <Route
                  path="/verify-email/:token"
                  element={<EmailVerification />}
                />
                <Route path="/verify-email" element={<EmailVerification />} />
                
                {/* Policy Routes */}
                <Route path="/policies">
                  <Route index element={<Navigate to="/policies/customer-service" replace />} />
                  <Route path="customer-service" element={<CustomerService />} />
                  <Route path="returns" element={<ReturnPolicy />} />
                  <Route path="refunds" element={<RefundPolicy />} />
                  <Route path="cancellations" element={<CancellationPolicy />} />
                  <Route path="privacy" element={<PrivacyPolicy />} />
                  <Route path="terms" element={<TermsAndConditions />} />
                </Route>
              </Routes>
            </div>
          </main>
          {/* Footer with Policy Links */}
          <footer className="bg-white dark:bg-gray-800 shadow-inner mt-16 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col md:flex-row justify-between items-center">
                <div className="mb-4 md:mb-0">
                  <Link to="/" className="flex items-center">
                    <img
                      src="/ryscircus-logo.svg"
                      alt="Ry's Circus Logo"
                      className="h-8 w-auto mr-2"
                    />
                    <span className="text-lg font-bold text-gray-800 dark:text-white font-handwritten">
                      Ry&apos;s Circus
                    </span>
                  </Link>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    &copy; {new Date().getFullYear()} Ry&apos;s Circus. All rights reserved.
                  </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Customer Support
                    </h3>
                    <ul className="mt-4 space-y-2">
                      <li>
                        <Link
                          to="/policies/customer-service"
                          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                        >
                          Contact Us
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/policies/returns"
                          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                        >
                          Return Policy
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/policies/refunds"
                          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                        >
                          Refund & Dispute Policy
                        </Link>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Legal
                    </h3>
                    <ul className="mt-4 space-y-2">
                      <li>
                        <Link
                          to="/policies/privacy"
                          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                        >
                          Privacy Policy
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/policies/terms"
                          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                        >
                          Terms & Conditions
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/policies/cancellations"
                          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                        >
                          Cancellation Policy
                        </Link>
                      </li>
                    </ul>
                  </div>

                  <div className="col-span-2 md:col-span-1">
                    <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Contact Information
                    </h3>
                    <ul className="mt-4 space-y-2">
                      <li className="text-sm text-gray-500 dark:text-gray-400">
                        <span className="block">Ry&apos;s Circus</span>
                      </li>
                      <li>
                        <a
                          href="mailto:support@ryscircus.com"
                          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                        >
                          support@ryscircus.com
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
